import type { Attribute } from '../types/prioritization';
import type { Candidate } from '../types/prioritization/lifecycle';
import { useAttributesStore } from '../stores/prioritization/attributesStore';
import { useDefinitionPolicyStore } from '../stores/prioritization/definitionPolicyStore';
import { useValueProposalStore } from '../stores/prioritization/valueProposalStore';
import { STRUCTURAL_FIELD_METAS, type DefinitionBehavior } from '../types/prioritization/definitionPolicy';
import type { Item } from '../types/prioritization/lifecycle';
import type {
  CriterionResult,
  ReadinessDefinition,
  ReadinessEvalResult,
} from '../types/prioritization/readinessDefinition';
import { evaluateItemReadiness } from './readinessEvaluator';
import { isOfficialValue } from './valueScope';

// ============================================================================
// Contextual Readiness — per cycle/track/session
// ============================================================================

/**
 * Describes the context in which an item's readiness is being evaluated.
 * Supplied by a track (category/altitude requirements) and optionally a
 * session/criteria config (required attribute keys).
 */
export interface ReadinessContext {
  /** Human-readable label for the context (e.g. cycle or session name) */
  label?: string;
  /** The cycle this context belongs to */
  cycleId?: string;
  /** The track that defines category/altitude requirements */
  trackId?: string;
  /** Required item category for Track Cell fit (omit for unscoped tracks) */
  requiredItemCategory?: string;
  /** Required altitude level for Track Cell fit (omit for unscoped tracks) */
  requiredAltitudeLevel?: number;
  /** Attribute keys that must have a non-empty value (from session criteria) */
  requiredAttributeKeys?: string[];
  /** Attribute keys that must have an *accepted* value (from reconciliationRequiredSet) */
  reconciledAttributeKeys?: string[];
}

export type ItemReadinessSeverity =
  | 'required'
  | 'recommended'
  | 'context-dependent'
  | 'informational';

export type ItemReadinessSection =
  | 'identity'
  | 'values'
  | 'relationships'
  | 'workflow';

export type ItemReadinessFixTarget =
  | 'item-top'
  | 'item-section-classification'
  | 'item-section-relationships'
  | 'item-section-values'
  | 'item-section-clarifications';

export interface ItemReadinessIssue {
  code: string;
  severity: ItemReadinessSeverity;
  section: ItemReadinessSection;
  label: string;
  displayText: string;
  /** What is missing, as a sentence ("No category."). Shown on the item page. */
  missingText: string;
  /** The every-item rule key this issue comes from ('itemCategory', 'attr:impact'). */
  fieldKey: string;
  fixTarget?: ItemReadinessFixTarget;
  attributeKey?: string;
}

export interface ItemReadinessResult {
  isReady: boolean;
  required: ItemReadinessIssue[];
  recommended: ItemReadinessIssue[];
  contextDependent: ItemReadinessIssue[];
  informational: ItemReadinessIssue[];
  all: ItemReadinessIssue[];
}

export interface LegacyReadinessMirror {
  readyForRanking: boolean;
  shapingCompleteness: number;
}

function hasMeaningfulValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * The item's values, from wherever they were captured. A value is a named input
 * whatever path set it (canon 24 §3): the item's own attribute list, and the
 * workspace's official accepted values — which is where a value accepted in a
 * session lands. Another session's value never counts here (lib/valueScope.ts).
 */
export function itemValues(item: Candidate): Array<{ attributeKey: string; value: unknown }> {
  return getAttributeValues(item);
}

/** True when the item has a value for the attribute, from any capture path. */
export function itemHasValue(item: Candidate, attributeKey: string): boolean {
  return valueIsSet(getAttributeValues(item), attributeKey);
}

function getAttributeValues(item: Candidate): Array<{ attributeKey: string; value: unknown }> {
  const maybeValues =
    (item as unknown as { attributes?: Array<{ attributeKey: string; value: unknown }> }).attributes
    ?? (item as unknown as { attributeValues?: Array<{ attributeKey: string; value: unknown }> }).attributeValues
    ?? [];
  const own = Array.isArray(maybeValues) ? maybeValues : [];
  const accepted = useValueProposalStore
    .getState()
    .acceptedValues.filter((v) => v.itemId === item.id && isOfficialValue(v))
    .map((v) => ({ attributeKey: v.attributeKey, value: v.value as unknown }));
  return accepted.length > 0 ? [...own, ...accepted] : own;
}

function valueIsSet(values: Array<{ attributeKey: string; value: unknown }>, key: string): boolean {
  return values.some((v) => v.attributeKey === key && hasMeaningfulValue(v.value));
}

function resolveRequiredAttributes(
  requiredAttributes?: Array<Pick<Attribute, 'key' | 'label' | 'required'>>
): Array<Pick<Attribute, 'key' | 'label' | 'required'>> {
  if (requiredAttributes) return requiredAttributes;
  return useAttributesStore.getState().getRequiredAttributes();
}

/**
 * Map a DefinitionBehavior to an ItemReadinessSeverity.
 * Returns null when the behavior is 'optional', meaning the field should be skipped.
 */
function behaviorToSeverity(behavior: DefinitionBehavior): ItemReadinessSeverity | null {
  if (behavior === 'block') return 'required';
  if (behavior === 'warn') return 'recommended';
  return null;
}

function resolveFieldSeverity(
  fieldKey: string,
  // NOTE: unused — severity is fully delegated to the definition policy
  // store's getBehavior()/behaviorToSeverity() below. Every call site's
  // literal here has zero runtime effect; kept as a documented default for
  // readers (and because removing the parameter would touch every call
  // site's signature — a behavior-adjacent decision left for a follow-up,
  // not folded into this type-fix pass).
  hardcodedDefault: ItemReadinessSeverity,
  attributeFallback?: Pick<Attribute, 'required'>
): ItemReadinessSeverity | null {
  void hardcodedDefault;
  const behavior = useDefinitionPolicyStore.getState().getBehavior(fieldKey, attributeFallback);
  return behaviorToSeverity(behavior);
}

function getRequiredCompletionStats(
  item: Candidate,
  requiredAttributes: Array<Pick<Attribute, 'key' | 'label' | 'required'>>
): { completed: number; total: number } {
  const attributeValues = getAttributeValues(item);
  const policy = useDefinitionPolicyStore.getState();
  let total = 0;
  let completed = 0;

  if (policy.getBehavior('itemCategory') === 'block') {
    total += 1;
    if (item.itemCategory) completed += 1;
  }

  if (policy.getBehavior('altitudeLevel') === 'block') {
    total += 1;
    if (item.altitudeLevel) completed += 1;
  }

  if (policy.getBehavior('dedupeStatus') === 'block') {
    total += 1;
    if (item.dedupeStatus && item.dedupeStatus !== 'pending') completed += 1;
  }

  if (item.dedupeStatus === 'duplicate_of') {
    total += 1;
    if (item.duplicateOfId) completed += 1;
  }

  for (const attribute of requiredAttributes) {
    const severity = resolveFieldSeverity(`attr:${attribute.key}`, attribute.required ? 'required' : 'recommended', {
      required: attribute.required,
    });
    if (severity !== 'required') continue;
    total += 1;
    const matchingValue = attributeValues.find((value) => value.attributeKey === attribute.key);
    if (matchingValue && hasMeaningfulValue(matchingValue.value)) {
      completed += 1;
    }
  }

  return { completed, total };
}

// ============================================================================
// Completeness — the every-item rules, no cycle context required
// ============================================================================

export interface BaseCompletenessResult {
  hasTitle: boolean;
  hasCategory: boolean;
  hasAltitude: boolean;
  /** @deprecated the duplicate check is an every-item rule like any other; read isBaseComplete */
  hasDedupe: boolean;
  /**
   * True when every Hard Stop in the workspace's every-item rules is met
   * (Settings → Readiness → Every item). This is Completeness (canon 24 §3).
   */
  isBaseComplete: boolean;
}

/**
 * Is the item complete under the workspace's every-item rules? The raw facts
 * (title, category, altitude) are reported as they are; whether the item counts
 * as complete is the rules' answer, not a fixed list of fields.
 */
export function computeBaseCompleteness(item: Candidate): BaseCompletenessResult {
  const hasTitle    = typeof item.title === 'string' && item.title.trim().length > 0;
  const hasCategory = Boolean(item.itemCategory);
  const hasAltitude = Boolean(item.altitudeLevel);

  return {
    hasTitle,
    hasCategory,
    hasAltitude,
    hasDedupe: true,
    isBaseComplete: hasTitle && computeItemReadiness(item).isReady,
  };
}

/**
 * The every-item rules the item does not meet that are about the item's own
 * description (not its values, which are assessment work), at Warning or Hard
 * Stop. This is "needs definition": lib/intakeQueueVisibility.ts reads it.
 */
export function everyItemDefinitionGaps(item: Candidate): ItemReadinessIssue[] {
  const readiness = computeItemReadiness(item, []);
  return [...readiness.required, ...readiness.recommended].filter((issue) => issue.section !== 'values');
}

// ============================================================================
// Contextual Readiness — per cycle / track / session
// ============================================================================

export interface ContextualReadinessResult {
  /** True when no required issues exist for this context */
  isReady: boolean;
  /** Category/altitude mismatch issues (wrong Track Cell) */
  trackFitIssues: ItemReadinessIssue[];
  /** Missing attribute value issues for this context's criteria */
  attributeIssues: ItemReadinessIssue[];
  all: ItemReadinessIssue[];
}

const UNSCOPED_CATEGORY = 'UNSCOPED';
const UNSCOPED_ALTITUDE = -1;

/**
 * Evaluates an item's readiness against a specific cycle/track/session context.
 * - Track fit: checks category and altitude match.
 * - Attribute coverage: checks required and reconciled attribute keys.
 */
export function computeContextualReadiness(
  item: Candidate,
  context: ReadinessContext,
): ContextualReadinessResult {
  const issues: ItemReadinessIssue[] = [];
  const trackFitIssues: ItemReadinessIssue[] = [];
  const attributeIssues: ItemReadinessIssue[] = [];
  const attributeValues = getAttributeValues(item);

  const requiredCategory = context.requiredItemCategory;
  const requiredAltitude = context.requiredAltitudeLevel;

  // Track Cell fit: only check if the track specifies a concrete scope
  if (requiredCategory && requiredCategory !== UNSCOPED_CATEGORY) {
    if (item.itemCategory !== requiredCategory) {
      const issue: ItemReadinessIssue = {
        code: 'wrong_item_category',
        severity: 'required',
        section: 'identity',
        label: 'Wrong category for this track',
        displayText: `Category must be "${requiredCategory}" for this track`,
        missingText: `Category must be "${requiredCategory}" for this track.`,
        fieldKey: 'itemCategory',
        fixTarget: 'item-section-classification',
      };
      trackFitIssues.push(issue);
      issues.push(issue);
    }
  }

  if (requiredAltitude !== undefined && requiredAltitude !== UNSCOPED_ALTITUDE) {
    if (item.altitudeLevel !== requiredAltitude) {
      const issue: ItemReadinessIssue = {
        code: 'wrong_altitude',
        severity: 'required',
        section: 'identity',
        label: 'Wrong altitude for this track',
        displayText: `Altitude must be Level ${requiredAltitude} for this track`,
        missingText: `Altitude must be Level ${requiredAltitude} for this track.`,
        fieldKey: 'altitudeLevel',
        fixTarget: 'item-section-classification',
      };
      trackFitIssues.push(issue);
      issues.push(issue);
    }
  }

  // Attribute coverage
  for (const key of context.requiredAttributeKeys ?? []) {
    const match = attributeValues.find((v) => v.attributeKey === key);
    if (!match || !hasMeaningfulValue(match.value)) {
      const issue: ItemReadinessIssue = {
        code: 'missing_attribute_value',
        severity: 'required',
        section: 'values',
        label: `Missing value: ${key}`,
        displayText: `Missing value for attribute: ${key}`,
        missingText: `${key} has no value.`,
        fieldKey: `attr:${key}`,
        fixTarget: 'item-section-values',
        attributeKey: key,
      };
      attributeIssues.push(issue);
      issues.push(issue);
    }
  }

  // Reconciled attribute coverage (accepted values required for session eligibility)
  for (const key of context.reconciledAttributeKeys ?? []) {
    const match = attributeValues.find((v) => v.attributeKey === key);
    const isAccepted =
      match &&
      hasMeaningfulValue(match.value) &&
      (match as { accepted?: boolean }).accepted !== false;
    if (!isAccepted) {
      const issue: ItemReadinessIssue = {
        code: 'missing_accepted_value',
        severity: 'required',
        section: 'values',
        label: `Unaccepted value: ${key}`,
        displayText: `Accepted value required for: ${key}`,
        missingText: `${key} has no accepted value.`,
        fieldKey: `attr:${key}`,
        fixTarget: 'item-section-values',
        attributeKey: key,
      };
      attributeIssues.push(issue);
      issues.push(issue);
    }
  }

  return {
    isReady: issues.length === 0,
    trackFitIssues,
    attributeIssues,
    all: issues,
  };
}

export function hasItemReadinessProgress(item: Candidate): boolean {
  const attributeValues = getAttributeValues(item);
  return Boolean(
    item.itemCategory
    || item.altitudeLevel
    || (item.dedupeStatus && item.dedupeStatus !== 'pending')
    || attributeValues.some((value) => hasMeaningfulValue(value.value))
    || (typeof item.desiredOutcome === 'string' && item.desiredOutcome.trim().length > 0)
    || (typeof item.acceptanceCriteria === 'string' && item.acceptanceCriteria.trim().length > 0)
  );
}

export function computeItemReadiness(
  item: Candidate,
  requiredAttributes?: Array<Pick<Attribute, 'key' | 'label' | 'required'>>
): ItemReadinessResult {
  const resolvedRequiredAttributes = resolveRequiredAttributes(requiredAttributes);
  const issues: ItemReadinessIssue[] = [];
  const attributeValues = getAttributeValues(item);

  if (!item.itemCategory) {
    const severity = resolveFieldSeverity('itemCategory', 'required');
    if (severity) {
      issues.push({
        code: 'missing_item_category',
        severity,
        section: 'identity',
        label: 'Set item category',
        displayText: 'Set item category',
        missingText: 'No category.',
        fieldKey: 'itemCategory',
        fixTarget: 'item-section-classification',
      });
    }
  }

  if (!item.altitudeLevel) {
    const severity = resolveFieldSeverity('altitudeLevel', 'required');
    if (severity) {
      issues.push({
        code: 'missing_altitude',
        severity,
        section: 'identity',
        label: 'Set altitude',
        displayText: 'Set altitude',
        missingText: 'No altitude.',
        fieldKey: 'altitudeLevel',
        fixTarget: 'item-section-classification',
      });
    }
  }

  if (!item.dedupeStatus || item.dedupeStatus === 'pending') {
    const severity = resolveFieldSeverity('dedupeStatus', 'informational');
    if (severity) {
      issues.push({
        code: 'missing_dedupe_decision',
        severity,
        section: 'relationships',
        label: 'Resolve duplicate status',
        displayText: 'Resolve duplicate status',
        missingText: 'Nobody has said whether this is a duplicate.',
        fieldKey: 'dedupeStatus',
        fixTarget: 'item-section-relationships',
      });
    }
  }

  if (item.dedupeStatus === 'duplicate_of' && !item.duplicateOfId) {
    // When an item is explicitly marked as duplicate_of, the canonical link
    // is always required regardless of policy — this is structural integrity.
    issues.push({
      code: 'missing_canonical_duplicate',
      severity: 'required',
      section: 'relationships',
      label: 'Select canonical item',
      displayText: 'Select canonical item',
      missingText: 'Marked a duplicate, but not of which item.',
      fieldKey: 'dedupeStatus',
      fixTarget: 'item-section-relationships',
    });
  }

  // All attributes (required-flagged or not) are evaluated against the definition policy.
  // The attribute's own `required` flag sets the code default; the policy can override it.
  for (const attribute of resolvedRequiredAttributes) {
    const fieldKey = `attr:${attribute.key}`;
    const severity = resolveFieldSeverity(fieldKey, attribute.required ? 'required' : 'recommended', {
      required: attribute.required,
    });
    if (!severity) continue;

    if (!valueIsSet(attributeValues, attribute.key)) {
      issues.push({
        code: 'missing_required_value',
        severity,
        section: 'values',
        label: 'Define value',
        displayText: `Define ${attribute.label}`,
        missingText: `${attribute.label} has no value.`,
        fieldKey,
        fixTarget: 'item-section-values',
        attributeKey: attribute.key,
      });
    }
  }

  if (!item.parentItemId) {
    const severity = resolveFieldSeverity('parentItemId', 'recommended');
    if (severity) {
      issues.push({
        code: 'missing_parent_item',
        severity,
        section: 'relationships',
        label: 'Add parent item',
        displayText: 'Add parent item',
        missingText: 'No parent item.',
        fieldKey: 'parentItemId',
        fixTarget: 'item-section-relationships',
      });
    }
  }

  if (!item.desiredOutcome && !item.acceptanceCriteria) {
    const severity = resolveFieldSeverity('rationale', 'recommended');
    if (severity) {
      issues.push({
        code: 'missing_rationale',
        severity,
        section: 'workflow',
        label: 'Add rationale',
        displayText: 'Add rationale',
        missingText: 'No desired outcome or acceptance criteria.',
        fieldKey: 'rationale',
        fixTarget: 'item-top',
      });
    }
  }

  if (!item.ownerId) {
    const severity = resolveFieldSeverity('ownerId', 'informational');
    if (severity) {
      issues.push({
        code: 'missing_owner',
        severity,
        section: 'identity',
        label: 'Assign owner',
        displayText: 'Assign owner',
        missingText: 'No one owns this item.',
        fieldKey: 'ownerId',
        fixTarget: 'item-top',
      });
    }
  }

  if (!item.beneficiary) {
    const severity = resolveFieldSeverity('beneficiary', 'informational');
    if (severity) {
      issues.push({
        code: 'missing_beneficiary',
        severity,
        section: 'identity',
        label: 'Set beneficiary',
        displayText: 'Set beneficiary',
        missingText: 'No beneficiary.',
        fieldKey: 'beneficiary',
        fixTarget: 'item-top',
      });
    }
  }

  if (!item.strategicBucket) {
    const severity = resolveFieldSeverity('strategicBucket', 'informational');
    if (severity) {
      issues.push({
        code: 'missing_strategic_bucket',
        severity,
        section: 'identity',
        label: 'Set strategic bucket',
        displayText: 'Set strategic bucket',
        missingText: 'No strategic bucket.',
        fieldKey: 'strategicBucket',
        fixTarget: 'item-section-classification',
      });
    }
  }

  if (!item.metaCategory) {
    const severity = resolveFieldSeverity('metaCategory', 'informational');
    if (severity) {
      issues.push({
        code: 'missing_meta_category',
        severity,
        section: 'identity',
        label: 'Set meta-category',
        displayText: 'Set meta-category',
        missingText: 'No meta-category.',
        fieldKey: 'metaCategory',
        fixTarget: 'item-section-classification',
      });
    }
  }

  const required = issues.filter((issue) => issue.severity === 'required');
  const recommended = issues.filter((issue) => issue.severity === 'recommended');
  const contextDependent = issues.filter((issue) => issue.severity === 'context-dependent');
  const informational = issues.filter((issue) => issue.severity === 'informational');

  return {
    isReady: required.length === 0,
    required,
    recommended,
    contextDependent,
    informational,
    all: issues,
  };
}

export function getBlockingReadinessIssues(
  item: Candidate,
  requiredAttributes?: Array<Pick<Attribute, 'key' | 'label' | 'required'>>
): ItemReadinessIssue[] {
  return computeItemReadiness(item, requiredAttributes).required;
}

export function isItemReadyForPrioritization(
  item: Candidate,
  requiredAttributes?: Array<Pick<Attribute, 'key' | 'label' | 'required'>>
): boolean {
  return computeItemReadiness(item, requiredAttributes).isReady;
}

export function computeLegacyReadinessMirror(
  item: Candidate,
  requiredAttributes?: Array<Pick<Attribute, 'key' | 'label' | 'required'>>
): LegacyReadinessMirror {
  const resolvedRequiredAttributes = resolveRequiredAttributes(requiredAttributes);
  const readiness = computeItemReadiness(item, resolvedRequiredAttributes);
  const { completed, total } = getRequiredCompletionStats(item, resolvedRequiredAttributes);

  return {
    readyForRanking: readiness.isReady,
    shapingCompleteness: total > 0 ? (completed / total) * 100 : 0,
  };
}

// ============================================================================
// Ready for ranking — readiness definition + every-item Hard Stops
// ============================================================================

function everyItemCriterion(issue: ItemReadinessIssue): CriterionResult {
  const criterion = issue.fieldKey.startsWith('attr:')
    ? {
        type: 'attribute_values' as const,
        attributeKey: issue.fieldKey.slice(5),
        label: issue.displayText.replace(/^Define /, ''),
      }
    : {
        type: 'field_populated' as const,
        fieldKey: issue.fieldKey,
        label: STRUCTURAL_FIELD_METAS.find((m) => m.fieldKey === issue.fieldKey)?.label ?? issue.label,
      };
  return {
    criterion,
    pass: false,
    reason: issue.missingText,
    remediationRole: 'Item owner',
    remediationType: 'configuration',
    everyItem: true,
  };
}

function criterionFieldKey(result: CriterionResult): string | null {
  const c = result.criterion;
  if (c.type === 'field_populated') return c.fieldKey;
  if (c.type === 'attribute_values' && !c.requiredValues?.length) return `attr:${c.attributeKey}`;
  return null;
}

/**
 * Is this item ready to be ranked here? Readiness includes completeness (canon
 * 24 §3): the answer is the item's unmet every-item Hard Stops plus the
 * criteria of the readiness definition in force (null = none set; the
 * every-item rules still apply). A definition criterion that repeats an
 * every-item Hard Stop is reported once.
 *
 * Accepted values default to the item's official values from the value store.
 */
export function evaluateReadyForRanking(
  item: Item,
  definition: ReadinessDefinition | null,
  acceptedValues?: Record<string, unknown>,
  agentSignoffs: Record<string, boolean> = {},
): ReadinessEvalResult {
  const values =
    acceptedValues ??
    Object.fromEntries(getAttributeValues(item as unknown as Candidate).map((v) => [v.attributeKey, v.value]));
  const hardStops = computeItemReadiness(item as unknown as Candidate).required.map(everyItemCriterion);
  const covered = new Set(hardStops.map(criterionFieldKey));
  const own = definition ? evaluateItemReadiness(item, definition, values, agentSignoffs).criteria : [];
  const criteria = [...hardStops, ...own.filter((r) => !(r.pass === false && covered.has(criterionFieldKey(r))) )];
  const passCount = criteria.filter((r) => r.pass).length;
  return {
    itemId: item.id,
    definitionId: definition?.id ?? null,
    pass: passCount === criteria.length,
    criteria,
    passCount,
    totalCount: criteria.length,
  };
}
